/* eslint-disable no-console */
import { AppRoot } from './AppRoot';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga4';
import { initSentryInApp } from 'setup/logger/initSentryInApp';
import { TableStatePreserver } from 'views/issueTable/tableView/TableState';
import './index.scss';
import {
  getEnvironment,
  isDevEnv,
} from './shared/utils/environment/determineEnvironment';
import { shouldEnableServiceWorkerInDev } from './shared/utils/environment/variables';
import { setMuiLicenseKey } from './setup/mui';
import { initAllServices } from 'serviceWorker/services';
import { ENVIRONMENTS } from 'shared/utils/environment/types';
import { logInfo } from 'setup/logger/logInfo';
import { configurePageChannels } from 'services/serviceWorker/configurePageChannels';
import { apiVersionClient } from 'shared/domain/apiVersion/apiVersionClient';
import { getUiStoreState } from 'store/ui';
import {
  alignServiceWorkerWithPage,
  isServiceWorkerAlignmentFailed,
  recoverServiceWorkerAlignment,
  reportServiceWorkerAlignmentFailure,
} from 'services/serviceWorker/alignment';
import { isPublicDownloadRoute } from 'setup/navigation/isPublicDownloadRoute';
import { announceCurrentUserToServiceWorker } from 'services/serviceWorker/announceCurrentUserToServiceWorker';
import { ServiceWorkerUpdateRequired } from 'views/serviceWorkerUpdateRequired';
import { ensureServiceWorkerPresent } from 'services/serviceWorker/presence';

configurePageChannels();
// From the start: the API may refuse this version already at sign-in.
apiVersionClient.onVersionMismatch(() => getUiStoreState().actions.markVersionMismatch());

const env = getEnvironment();
const isSwEnabledInDevEnv = shouldEnableServiceWorkerInDev();
if (isDevEnv() && !isSwEnabledInDevEnv) {
  console.log(
    `Service worker disabled,initializing services on main thread`
  );
  initAllServices();
}
setMuiLicenseKey();
initSentryInApp();

if (env === ENVIRONMENTS.PRODUCTION) {
  ReactGA.initialize('G-8PWDQ2BWMT');
} else {
  ReactGA.initialize('G-PRR3K48C8M');
}

const tableState = new TableStatePreserver();

const container = document.getElementById('root');
const root = createRoot(container!);

/**
 * The app talks to the worker from its first render, so it starts once the
 * worker runs the page's version: no protocol of an older worker to
 * understand. Public downloads fall back to direct HTTP without a worker.
 */
async function isServiceWorkerReadyForApp(): Promise<boolean> {
  if (isDevEnv() && !isSwEnabledInDevEnv) {
    return true;
  }
  const attempt = await alignServiceWorkerWithPage();
  if (!isServiceWorkerAlignmentFailed(attempt.alignment)) {
    return true;
  }
  if (isPublicDownloadRoute(window.location.pathname)) {
    void reportServiceWorkerAlignmentFailure([attempt]);
    return true;
  }
  // The screen stays while the worker gets time to catch up (e.g. a host still serving the old worker).
  root.render(<ServiceWorkerUpdateRequired />);
  return !isServiceWorkerAlignmentFailed(await recoverServiceWorkerAlignment(attempt));
}

async function renderApp(): Promise<void> {
  if (!(await isServiceWorkerReadyForApp())) {
    return;
  }
  announceCurrentUserToServiceWorker();
  root.render(<AppRoot tableState={tableState} />);
  // The worker the app starts with is one it can lose: known from now, not from the first keep-alive.
  void isServiceWorkerPresentForKeepAlive();
}

void renderApp();

console.log('isDevEnv', isDevEnv());
console.log('shouldEnableServiceWorkerInDev', isSwEnabledInDevEnv);
if (isDevEnv() && !isSwEnabledInDevEnv) {
  console.log(
    `Service worker disabled,initializing services on main thread`
  );
}

function askForServiceWorkerRefresh(): void {
  // Public downloads work without the worker.
  if (!isPublicDownloadRoute(window.location.pathname)) {
    root.render(<ServiceWorkerUpdateRequired />);
  }
}

async function isServiceWorkerPresentForKeepAlive(): Promise<boolean> {
  // Development without the worker has none to keep alive or to lose.
  if (isDevEnv()) {
    return true;
  }
  return ensureServiceWorkerPresent(askForServiceWorkerRefresh);
}

// https://stackoverflow.com/questions/29741922/prevent-service-worker-from-automatically-stopping
async function pingKeepAlive(
  trigger: 'interval' | 'visibility' | 'focus'
): Promise<void> {
  // Without the worker the ping only fails: the page restores it, or asks for a refresh.
  if (!(await isServiceWorkerPresentForKeepAlive())) {
    return;
  }
  const start = performance.now();
  try {
    await fetch('/keep-alive');
  } catch (error) {
    const duration = Math.round(performance.now() - start);
    logInfo('keep-alive-metric-error', {
      duration_ms: duration,
      trigger,
      error: String(error),
    });
  }
}

function keepServiceWorkerAlive(): void {
  setInterval(() => pingKeepAlive('interval'), 20000);
}

keepServiceWorkerAlive();

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    pingKeepAlive('visibility');
  }
});

window.addEventListener('focus', () => {
  pingKeepAlive('focus');
});
